import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Link,
  TextField,
  InputBase,
  TextareaAutosize,
  AlertTitle,
  Tooltip,
  Badge
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SaveIcon from '@mui/icons-material/Save';
import CampaignIcon from '@mui/icons-material/Campaign';
import SendIcon from '@mui/icons-material/Send';
import Collapse from '@mui/material/Collapse';
import { ReactComponent as GuardiaLogo } from '../../assets/logo_guardia.svg';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ScrollContext } from '../Context';
import { ThemeContext } from '../Context';
import { EditingContext } from '../Context';
import { GuardiaContext } from '../Context';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import EmptyReportGuide from './EmptyReportGuide';
import axios from 'axios';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';
// Force le chargement local de TinyMCE v6
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce-i18n/langs/fr_FR';

// Plugins COMMUNITY uniquement (rien de premium)
import 'tinymce/plugins/lists';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/code';
import 'tinymce/plugins/image';
import 'tinymce/plugins/table';
import 'tinymce/plugins/searchreplace';

// CSS de skin/ui en local pour afficher l'UI sans chargement externe
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';

import { Editor } from '@tinymce/tinymce-react';
import GetUserAvatar from '../tool/GetUserAvatar';
import RubricForm from './RubricForm';
import GuardiaWrite from './GuardiaWrite';
import GuardiaCheck from './GuardiaCheck';
import { GuardiaAccessChecker } from './GuardiaAccessChecker';

// Configuration TinyMCE pour mode self-hosted gratuit
const buildTinymceConfig = (token) => ({
  menubar: false,
  language: 'fr_FR',
  plugins: [
    'lists', 'autoresize', 'wordcount', 'fullscreen', 'table', 'searchreplace', 'image'
  ],
  toolbar: 'undo redo | bold italic underline strikethrough | forecolor backcolor | bullist numlist blockquote | alignleft aligncenter alignright alignjustify | indent outdent | table | image | removeformat searchreplace | fullscreen',
  content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 15px; }',
  branding: false,
  elementpath: false,
  statusbar: false,
  paste_as_text: false,
  paste_webkit_styles: 'none',
  paste_remove_styles: false,
  paste_merge_formats: true,
  smart_paste: false,
  // Configuration pour mode self-hosted (pas de cloud)
  promotion: false,
  contextmenu: false,
  // Évite tout chargement externe
  skin: false,
  content_css: false,
  // Fullscreen natif du navigateur pour éviter les problèmes de z-index
  fullscreen_native: true,
  // Hauteur minimale souhaitée (~5 lignes)
  min_height: 200,
  // Auto-resize
  autoresize_min_height: 200,
  autoresize_max_height: 600,
  autoresize_bottom_margin: 16,
  images_upload_credentials: true,
  file_picker_types: 'image',
  image_description: false,
  images_upload_handler: async (blobInfo) => {
    const formData = new FormData();
    formData.append('image', blobInfo.blob(), blobInfo.filename());

    try {
      const response = await axios.post('/api/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        withCredentials: true
      });

      const url = response.data?.url;
      if (!url) {
        throw new Error("Réponse inattendue du serveur d'upload.");
      }

      return url;
    } catch (error) {
      const message = error.response?.data?.message || "Échec de l'upload de l'image.";
      throw new Error(message);
    }
  },
  paste_postprocess: (plugin, args) => {
    // Normalisation des espaces comme dans Quill
    args.node.innerHTML = args.node.innerHTML
      .replace(/<strong>([^<]+)<\/strong>\s+<strong>([^<]+)<\/strong>/g, '<strong>$1&nbsp;$2</strong>')
      .replace(/<strong><u>([^<]+)<\/u><\/strong>\s+<strong>([^<]+)<\/strong>/g, '<strong><u>$1</u>&nbsp;$2</strong>')
      .replace(/<u><strong>([^<]+)<\/strong><\/u>\s+<strong>([^<]+)<\/strong>/g, '<strong><u>$1</u>&nbsp;$2</strong>');
  }
});

// Normalisation des espaces entre balises inline pour éviter que Quill ne les colle
const normalizeEditorHtmlSpaces = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  let normalized = html;
  // Fusionner deux blocs strong avec un espace insécable unique
  normalized = normalized.replace(
    /<strong>([^<]+)<\/strong>\s+<strong>([^<]+)<\/strong>/g,
    '<strong>$1&nbsp;$2</strong>'
  );
  // Cas: <strong><u>NOM</u></strong> <strong>Prénom</strong> => <strong><u>NOM</u>&nbsp;Prénom</strong>
  normalized = normalized.replace(
    /<strong><u>([^<]+)<\/u><\/strong>\s+<strong>([^<]+)<\/strong>/g,
    '<strong><u>$1</u>&nbsp;$2</strong>'
  );
  // Cas: <u><strong>NOM</strong></u> <strong>Prénom</strong> => <strong><u>NOM</u>&nbsp;Prénom</strong>
  normalized = normalized.replace(
    /<u><strong>([^<]+)<\/strong><\/u>\s+<strong>([^<]+)<\/strong>/g,
    '<strong><u>$1</u>&nbsp;$2</strong>'
  );
  return normalized;
};

// Composant personnalisé pour l'éditeur TinyMCE
function RichTextEditor({ value, onChange, placeholder, isDark, userInsertions = [] }) {
  // Garder une référence aux insertions utilisateur pour usage dans les callbacks TinyMCE
  const insertionsRef = useRef(userInsertions);

  useEffect(() => {
    insertionsRef.current = Array.isArray(userInsertions) ? userInsertions : [];
  }, [userInsertions]);

  // Trouver une insertion dont le nom encadre la position du curseur (supporte les espaces)
  const findInsertionMatch = (text, offset) => {
    if (typeof text !== 'string') return null;
    const lowerText = text.toLowerCase();
    const isBoundary = (ch) =>
      ch === undefined || ch === '' || /\s|[.,;:!?()[\]{}"']/.test(ch);

    let bestMatch = null;

    insertionsRef.current.forEach((ins) => {
      const name = (ins?.name || '').trim();
      if (!name) return;
      const lowerName = name.toLowerCase();
      const nameLen = lowerName.length;

      // Rechercher une occurrence qui se termine autour du curseur
      const start = lowerText.lastIndexOf(lowerName, Math.max(0, offset));
      if (start === -1) return;
      const end = start + nameLen;

      // Le curseur doit être à l'intérieur ou juste à la fin du nom
      if (offset < start || offset > end) return;

      const prevChar = lowerText[start - 1];
      const nextChar = lowerText[end];

      // Vérifier que le nom est délimité par des bornes (début/fin/espaces/punctuation)
      if (!isBoundary(prevChar) || !isBoundary(nextChar)) return;

      if (!bestMatch || nameLen > bestMatch.length) {
        bestMatch = {
          start,
          end,
          length: nameLen,
          insertion: ins,
        };
      }
    });

    return bestMatch;
  };

  // Chercher un nom d'insertion autour du curseur et le remplacer par le contenu
  const tryReplaceWithInsertion = (editor) => {
    const sel = editor.selection?.getSel();
    if (!sel || !sel.anchorNode) return false;

    let node = sel.anchorNode;
    let offset = sel.anchorOffset;

    // Si on est sur un élément, essayer de prendre le nœud texte précédent
    if (node.nodeType !== 3 && node.childNodes && node.childNodes.length) {
      const idx = Math.min(offset, node.childNodes.length - 1);
      const candidate = node.childNodes[idx];
      if (candidate && candidate.nodeType === 3) {
        node = candidate;
        offset = candidate.data?.length || 0;
      }
    }

    if (node.nodeType !== 3) return false;

    const data = node.data || '';
    const match = findInsertionMatch(data, offset);
    if (!match || !match.insertion) return false;

    // Sélectionner le mot et insérer le contenu HTML de l'insertion
    const rng = editor.dom.createRng();
    rng.setStart(node, match.start);
    rng.setEnd(node, match.end);
    editor.selection.setRng(rng);
    editor.insertContent(match.insertion.content || '');
    return true;
  };

  const handleEditorChange = (content, editor) => {
    // Appliquer la normalisation des espaces
    const normalizedContent = normalizeEditorHtmlSpaces(content);
    onChange(normalizedContent);
  };

  const editorConfig = {
    ...buildTinymceConfig(sessionStorage.getItem('authToken')),
    placeholder: placeholder || "Écrivez ici le contenu de la rubrique...",
    content_style: `
      body {
        font-family: Helvetica, Arial, sans-serif;
        font-size: 15px;
        color: ${isDark ? '#ecf0f1' : 'inherit'};
        background-color: ${isDark ? '#22303e' : '#ffffff'};
      }
      .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
        color: ${isDark ? '#95a5a6' : '#aaa'};
      }
    `,
    setup: (editor) => {
      editor.on('keydown', (event) => {
        if (event.key === 'F3' || event.keyCode === 114) {
          const replaced = tryReplaceWithInsertion(editor);
          if (replaced) {
            event.preventDefault();
            event.stopPropagation();
          }
        }
      });
    }
  };

  return (
    <Box sx={{
      '& .tox-tinymce': {
        borderRadius: '4px',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
        minHeight: '120px'
      },
      '& .tox-toolbar': {
        backgroundColor: isDark ? '#21303c' : '#f5f5f5',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
      },
      '& .tox-editor-container': {
        backgroundColor: isDark ? '#22303e' : '#ffffff',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
      },
      mt: 1,
      mb: 5
    }}>
      <Editor
        value={normalizeEditorHtmlSpaces(value) || ''}
        init={editorConfig}
        onEditorChange={handleEditorChange}
      />
    </Box>
  );
}

// Composant pour afficher le contenu HTML avec la mise en forme
function RichTextDisplay({ content, isDark }) {
  if (!content) return (
    <Typography
      component="span"
      sx={{
        color: isDark ? '#95a5a6' : 'text.disabled',
        display: 'block',
        width: '100%',
        textAlign: 'center',
        fontStyle: 'italic'
      }}
    >
      Cette rubrique est vide.
    </Typography>
  );

  return (
    <Box
      sx={{
        '& .rich-text-content': {
          color: isDark ? '#ecf0f1' : 'inherit',
          fontSize: '15px',
          '& h1, & h2, & h3': {
            margin: '0.5em 0',
            fontWeight: 'bold',
            color: isDark ? '#ecf0f1' : 'inherit'
          },
          '& h1': { fontSize: '1.5em' },
          '& h2': { fontSize: '1.3em' },
          '& h3': { fontSize: '1.1em' },
          '& p': {
            margin: '0.5em 0',
            color: isDark ? '#ecf0f1' : 'inherit'
          },
          '& ul, & ol': {
            paddingLeft: '2em',
            margin: '0.5em 0',
            color: isDark ? '#ecf0f1' : 'inherit'
          },
          '& blockquote': {
            borderLeft: isDark ? '3px solid #4fc3f7' : '3px solid #ccc',
            paddingLeft: '1em',
            margin: '0.5em 0',
            fontStyle: 'italic',
            color: isDark ? '#bdc3c7' : 'inherit'
          },
          '& img': {
            maxWidth: '100%'
          },
          '& a': {
            color: isDark ? '#4fc3f7' : 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }
        }
      }}
    >
      <div
        dangerouslySetInnerHTML={{ __html: content }}
        style={{
          fontFamily: 'inherit'
        }}
        className="rich-text-content"
      />
    </Box>
  );
}

// Composant pour afficher le texte corrigé avec les modifications mises en évidence
function CorrectedTextDisplay({ originalText, correctedText, differences, isDark }) {
  // Si aucune différence, afficher le texte normal
  if (!differences || differences.length === 0) {
    return (
      <Typography
        sx={{
          color: isDark ? '#ecf0f1' : 'text.primary',
          whiteSpace: 'pre-wrap',
          fontFamily: 'inherit'
        }}
      >
        {correctedText}
      </Typography>
    );
  }

  // Si le texte est identique, pas de changements
  if (originalText === correctedText) {
    return (
      <Typography
        sx={{
          color: isDark ? '#ecf0f1' : 'text.primary',
          whiteSpace: 'pre-wrap',
          fontFamily: 'inherit'
        }}
      >
        {correctedText}
      </Typography>
    );
  }

  // Afficher le texte avec les parties modifiées mises en évidence
  const parts = [];
  let lastIndex = 0;

  differences.forEach((diff, index) => {
    if (diff.original && diff.position) {
      // Texte avant la différence
      if (diff.position.start > lastIndex) {
        parts.push({
          text: correctedText.slice(lastIndex, diff.position.start),
          type: 'normal'
        });
      }

      // Texte corrigé mis en évidence
      parts.push({
        text: diff.corrected,
        type: 'corrected',
        explanation: diff.explanation,
        original: diff.original
      });

      lastIndex = diff.position.end;
    }
  });

  // Texte après la dernière différence
  if (lastIndex < correctedText.length) {
    parts.push({
      text: correctedText.slice(lastIndex),
      type: 'normal'
    });
  }

  return (
    <Typography
      sx={{
        color: isDark ? '#ecf0f1' : 'text.primary',
        whiteSpace: 'pre-wrap',
        fontFamily: 'inherit',
        lineHeight: 1.6
      }}
    >
      {parts.map((part, index) => {
        if (part.type === 'corrected') {
          return (
            <Tooltip
              key={index}
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Correction appliquée :
                  </Typography>
                  <Typography variant="body2">
                    "{part.original}" → "{part.text}"
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {part.explanation}
                  </Typography>
                </Box>
              }
              placement="top"
              arrow
            >
              <span
                style={{
                  backgroundColor: isDark ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 215, 0, 0.4)',
                  borderRadius: '3px',
                  padding: '2px 4px',
                  border: `1px solid ${isDark ? 'rgba(255, 215, 0, 0.6)' : 'rgba(255, 215, 0, 0.8)'}`,
                  cursor: 'help',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = isDark ? 'rgba(255, 215, 0, 0.4)' : 'rgba(255, 215, 0, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isDark ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 215, 0, 0.4)';
                }}
              >
                {part.text}
              </span>
            </Tooltip>
          );
        }
        return <span key={index}>{part.text}</span>;
      })}
    </Typography>
  );
}

function Edit() {
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  console.log('Edit.js - Socket context:', socket ? 'Connected' : 'Not connected');
  const { targetRubric, scrollToBottomTrigger } = useContext(ScrollContext);
  const { theme } = useContext(ThemeContext);
  const { startEditingRubric, stopEditingRubric } = useContext(EditingContext);
  const { addTextToGuardiaInput, addCitation } = useContext(GuardiaContext);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';
  
  // État pour détecter le mode fullscreen de TinyMCE
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);

  // Observer pour détecter quand TinyMCE entre en mode fullscreen
  useEffect(() => {
    const checkFullscreen = () => {
      setIsEditorFullscreen(document.body.classList.contains('tox-fullscreen'));
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkFullscreen();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    checkFullscreen();

    return () => observer.disconnect();
  }, []);
  
  // Ajouter des logs pour débogage
  useEffect(() => {
    console.log('UserContext user:', user);
  }, [user]);
  
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [rubricToDelete, setRubricToDelete] = useState(null);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [rubricToCancel, setRubricToCancel] = useState(null);
  const [editTitles, setEditTitles] = useState({});
  const [editContents, setEditContents] = useState({});
  const [stableRubrics, setStableRubrics] = useState({}); // Pour garder des copies stables des rubriques en génération
  const [guardiaAccess, setGuardiaAccess] = useState({ hasAccess: false, reason: '' });
  const [guardiaListenAccess, setGuardiaListenAccess] = useState({ hasAccess: false, reason: '' });
  const [guardiaChatAccess, setGuardiaChatAccess] = useState({ hasAccess: false, reason: '' });
  const [userInsertions, setUserInsertions] = useState([]);
  
  // États pour les commentaires
  const [openCommentBoxes, setOpenCommentBoxes] = useState({}); // {rubricId: true/false}
  const [commentTexts, setCommentTexts] = useState({}); // {rubricId: 'texte'}
  const [editingComments, setEditingComments] = useState({}); // {commentId: 'texte'}
  
  // Références aux rubriques pour le défilement
  const rubricRefs = useRef({});
  const containerRef = useRef(null);

  // Vérifier l'accès GuardIA au chargement
  useEffect(() => {
    const checkGuardiaAccess = async () => {
      const [checkAccess, listenAccess, chatAccess] = await Promise.all([
        GuardiaAccessChecker.checkFeatureAccess('check'),
        GuardiaAccessChecker.checkFeatureAccess('listen'),
        GuardiaAccessChecker.checkFeatureAccess('chat')
      ]);
      setGuardiaAccess(checkAccess);
      setGuardiaListenAccess(listenAccess);
      setGuardiaChatAccess(chatAccess);
    };
    
    checkGuardiaAccess();
  }, []);

  // Charger les insertions personnelles de l'utilisateur (pour remplacements F3)
  useEffect(() => {
    const fetchInsertions = async () => {
      const userMatricule = typeof user === 'object' ? user?.matricule : user;
      if (!userMatricule) {
        setUserInsertions([]);
        return;
      }

      try {
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/user/${userMatricule}/insertions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = Array.isArray(response.data?.myInsertions) ? response.data.myInsertions : [];
        setUserInsertions(list);
      } catch (error) {
        console.error('Erreur lors du chargement des insertions utilisateur:', error);
        setUserInsertions([]);
      }
    };

    fetchInsertions();
  }, [user]);

  // Charger les données du rapport
  useEffect(() => {
    console.log('ReportContext report:', report);
    
    if (!report) return;

    const fetchReportData = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/report/search`, {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        
        setReportData(response.data);
        
        // Initialiser les états pour l'édition
        const titles = {};
        const contents = {};
        const stables = {};
        const userMatricule = typeof user === 'object' ? user?.matricule : user;
        
        response.data.rubrics.forEach(rubric => {
          titles[rubric.id] = rubric.title;
          contents[rubric.id] = rubric.content;
          
          // Créer une copie stable pour les rubriques non générées en cours d'édition par l'utilisateur
          if (!rubric.generated && rubric.isEditing && rubric.editedBy === userMatricule) {
            stables[rubric.id] = { ...rubric };
          }
        });
        
        setEditTitles(titles);
        setEditContents(contents);
        setStableRubrics(stables);
        setReportError(null);
        setLoadError(null);
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données du rapport:', error);
        if (reportData) {
          setReportError('Connexion à la base interrompue. Les données affichées peuvent être obsolètes.');
        } else {
          setLoadError('Impossible de charger les rubriques du rapport. Veuillez réessayer.');
        }
        setLoading(false);
      }
    };

    fetchReportData();
  }, [report]);

  // Écouter les mises à jour du rapport via WebSocket
  useEffect(() => {
    if (!socket || !report) return;

    const handleReportUpdate = (updatedReport) => {
      console.log('Mise à jour de rapport reçue:', updatedReport);
      
      if (updatedReport.number === report.number && updatedReport.year === report.year) {
        setReportData(prevData => {
          if (!prevData) return null;
          
          // Mettre à jour tous les champs sauf les rubriques d'abord
          const updates = { ...updatedReport };
          
          // Traitement spécial pour les rubriques
          if (updatedReport.rubrics) {
            console.log('Mise à jour des rubriques reçue:', updatedReport.rubrics);
            
            // Protéger les modifications en cours : ne mettre à jour editTitles et editContents
            // que pour les rubriques qui ne sont pas en cours d'édition par l'utilisateur actuel
            const userMatricule = typeof user === 'object' ? user?.matricule : user;
            const newTitles = {...editTitles};
            const newContents = {...editContents};
            
            // Fusionner les rubriques en préservant les états d'édition locaux
            const mergedRubrics = updatedReport.rubrics.map(updatedRubric => {
              const existingRubric = prevData.rubrics?.find(r => r.id === updatedRubric.id);
              
              // Si cette rubrique est en cours d'édition par l'utilisateur actuel, préserver l'état local
              // MAIS toujours prendre les commentaires de la version mise à jour
              if (existingRubric && existingRubric.isEditing && existingRubric.editedBy === userMatricule) {
                console.log(`Préservation de l'état d'édition local pour la rubrique ${updatedRubric.id}, avec fusion des commentaires`);
                return {
                  ...existingRubric,
                  comments: updatedRubric.comments || existingRubric.comments || []
                };
              }
              
              // Sinon, utiliser la version mise à jour
              return updatedRubric;
            });
            
            // Mettre à jour editTitles et editContents seulement pour les nouvelles rubriques ou celles non éditées
            updatedReport.rubrics.forEach(rubric => {
              const existingRubric = prevData.rubrics?.find(r => r.id === rubric.id);
              const isCurrentlyEditingThis = existingRubric && existingRubric.isEditing && existingRubric.editedBy === userMatricule;
              
              if (!isCurrentlyEditingThis) {
                newTitles[rubric.id] = rubric.title;
                newContents[rubric.id] = rubric.content;
              }
            });
            
            setEditTitles(newTitles);
            setEditContents(newContents);
            
            updates.rubrics = mergedRubrics;
          }
          
          return {
            ...prevData,
            ...updates
          };
        });
      }
    };
    
    // Écouter également les événements émis directement par le client
    const handleDirectUpdate = (data) => {
      console.log('Mise à jour directe reçue:', data);
      
      if (data.reportId === `${report.number}-${report.year}`) {
        console.log('Mise à jour directe des données:', data);
        setReportData(prevData => {
          if (!prevData) return null;
          
          // Créer un objet avec les champs à mettre à jour
          const updates = {};
          
          // Liste des champs à mettre à jour (sauf rubrics qui nécessite un traitement spécial)
          const fieldsToUpdate = [
            'StartDate', 'EndDate', 'intervenants', 
            'unit', 'reportType', 'indice', 'personnesPhysiques', 
            'objets', 'vehicules'
          ];
          
          fieldsToUpdate.forEach(field => {
            if (data[field] !== undefined) {
              updates[field] = data[field];
            }
          });
          
          // Traitement spécial pour les rubriques
          if (data.rubrics) {
            console.log('Mise à jour directe des rubriques:', data.rubrics);
            
            // Protéger les modifications en cours
            const userMatricule = typeof user === 'object' ? user?.matricule : user;
            const newTitles = {...editTitles};
            const newContents = {...editContents};
            
            // Fusionner les rubriques en préservant les états d'édition locaux
            const mergedRubrics = data.rubrics.map(updatedRubric => {
              const existingRubric = prevData.rubrics?.find(r => r.id === updatedRubric.id);
              
              // Si cette rubrique est en cours d'édition par l'utilisateur actuel, préserver l'état local
              // MAIS toujours prendre les commentaires de la version mise à jour
              if (existingRubric && existingRubric.isEditing && existingRubric.editedBy === userMatricule) {
                console.log(`Préservation de l'état d'édition local pour la rubrique ${updatedRubric.id}, avec fusion des commentaires`);
                return {
                  ...existingRubric,
                  comments: updatedRubric.comments || existingRubric.comments || []
                };
              }
              
              // Sinon, utiliser la version mise à jour
              return updatedRubric;
            });
            
            // Mettre à jour editTitles et editContents seulement pour les nouvelles rubriques ou celles non éditées
            data.rubrics.forEach(rubric => {
              const existingRubric = prevData.rubrics?.find(r => r.id === rubric.id);
              const isCurrentlyEditingThis = existingRubric && existingRubric.isEditing && existingRubric.editedBy === userMatricule;
              
              if (!isCurrentlyEditingThis) {
                newTitles[rubric.id] = rubric.title;
                newContents[rubric.id] = rubric.content;
              }
            });
            
            setEditTitles(newTitles);
            setEditContents(newContents);
            
            updates.rubrics = mergedRubrics;
          }
          
          return {
            ...prevData,
            ...updates
          };
        });
      }
    };

    socket.on('reportUpdated', handleReportUpdate);
    socket.on('updateReport', handleDirectUpdate);

    // *** SUPPRIMÉ : Plus de joinReport/leaveReport individuel - géré par Core.js ***

    return () => {
      socket.off('reportUpdated', handleReportUpdate);
      socket.off('updateReport', handleDirectUpdate);
      // *** SUPPRIMÉ : Plus de joinReport/leaveReport individuel - géré par Core.js ***
    };
  }, [socket, report, editTitles, editContents, user]);

  // Gérer le défilement vers la rubrique ciblée
  useEffect(() => {
    if (targetRubric && rubricRefs.current[targetRubric]) {
      rubricRefs.current[targetRubric].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [targetRubric]);

  // Gérer le scroll vers le bas quand une nouvelle rubrique est ajoutée
  useEffect(() => {
    if (scrollToBottomTrigger > 0 && containerRef.current) {
      // Petit délai pour s'assurer que le DOM est mis à jour
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 50);
    }
  }, [scrollToBottomTrigger]);

  // Fonction pour déplacer une rubrique vers le haut ou vers le bas
  const moveRubric = async (rubricId, direction) => {
    if (!reportData || !reportData.rubrics) return;
    
    const rubrics = [...reportData.rubrics];
    const index = rubrics.findIndex(r => r.id === rubricId);
    
    if (index === -1) return;
    
    // Si on monte la première rubrique ou descend la dernière, on ne fait rien
    if ((direction === 'up' && index === 0) || 
        (direction === 'down' && index === rubrics.length - 1)) {
      return;
    }
    
    // Échanger les positions des rubriques
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Échanger les positions
    const temp = rubrics[index].position;
    rubrics[index].position = rubrics[targetIndex].position;
    rubrics[targetIndex].position = temp;
    
    // Tri des rubriques pour maintenir l'ordre
    const sortedRubrics = [...rubrics].sort((a, b) => a.position - b.position);
    
    // Mise à jour locale
    setReportData(prevData => ({
      ...prevData,
      rubrics: sortedRubrics
    }));
    
    try {
      // Mise à jour via API
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData._id;
      
      await axios.put(
        `/api/report/${reportId}/rubrics`,
        { rubrics: sortedRubrics },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        console.log('🚀 Edit.js - Émission updateReport et broadcastReportUpdate (sort)');
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          rubrics: sortedRubrics
        });

        socket.emit('broadcastReportUpdate', {
          number: report.number,
          year: report.year,
          rubrics: sortedRubrics
        });
      } else {
        console.warn('⚠️ Edit.js - Pas de socket disponible pour émettre les mises à jour (sort)');
      }

      // Log du déplacement de rubrique
      const movedRubric = rubrics.find(r => r.id === rubricId);
      logAction(LOG_ACTIONS.RUBRIC_MOVE, formatReportNumber(report.number, report.year), {
        rubricId: rubricId,
        rubricTitle: movedRubric?.title || null,
        direction: direction
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour des positions:', error);
      // Recharger les données en cas d'erreur
      const fetchReportData = async () => {
        try {
          const token = sessionStorage.getItem('authToken');
          const response = await axios.get(`/api/report/search`, {
            params: { number: report.number, year: report.year },
            headers: { Authorization: `Bearer ${token}` },
          });
          
          // Préserver les modifications en cours lors du rechargement
          const userMatricule = typeof user === 'object' ? user?.matricule : user;
          const newTitles = {...editTitles};
          const newContents = {...editContents};
          
          response.data.rubrics.forEach(rubric => {
            // Ne pas écraser si l'utilisateur actuel est en train d'éditer cette rubrique
            const isCurrentlyEditingThis = rubric.isEditing && rubric.editedBy === userMatricule;
            
            if (!isCurrentlyEditingThis) {
              newTitles[rubric.id] = rubric.title;
              newContents[rubric.id] = rubric.content;
            }
          });
          
          setEditTitles(newTitles);
          setEditContents(newContents);
          setReportData(response.data);
        } catch (error) {
          console.error('Erreur lors du rechargement des données:', error);
        }
      };
      
      fetchReportData();
    }
  };

  // Fonctions pour gérer la suppression d'une rubrique
  const handleOpenDeleteDialog = (rubricId) => {
    setRubricToDelete(rubricId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setRubricToDelete(null);
  };

  // Fonctions pour gérer la confirmation d'annulation d'édition
  const handleOpenCancelDialog = (rubricId) => {
    setRubricToCancel(rubricId);
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
    setRubricToCancel(null);
  };

  const handleConfirmCancel = () => {
    if (!rubricToCancel || !reportData || !reportData.rubrics) return;

    // Trouver la rubrique à annuler
    const rubric = reportData.rubrics.find(r => r.id === rubricToCancel);
    if (rubric) {
      cancelEditing(rubric);
    }

    handleCloseCancelDialog();
  };

  const handleDeleteRubric = async () => {
    if (!rubricToDelete || !reportData || !reportData.rubrics) return;
    
    try {
      const rubrics = [...reportData.rubrics];
      
      // Trouver la rubrique à supprimer AVANT de la retirer
      const deletedRubricData = rubrics.find(r => r.id === rubricToDelete);
      
      const updatedRubrics = rubrics.filter(r => r.id !== rubricToDelete);
      
      // Réorganiser les positions des rubriques
      updatedRubrics.forEach((rubric, index) => {
        rubric.position = index + 1;
      });
      
      // Nettoyer les états d'édition pour la rubrique supprimée
      setEditTitles(prev => {
        const newTitles = {...prev};
        delete newTitles[rubricToDelete];
        return newTitles;
      });
      
      setEditContents(prev => {
        const newContents = {...prev};
        delete newContents[rubricToDelete];
        return newContents;
      });
      
      // Nettoyer la copie stable si elle existe
      setStableRubrics(prev => {
        const newStableRubrics = {...prev};
        delete newStableRubrics[rubricToDelete];
        return newStableRubrics;
      });
      
      // Mise à jour locale
      setReportData(prevData => ({
        ...prevData,
        rubrics: updatedRubrics
      }));
      
      // Mise à jour via API avec les infos de la rubrique supprimée pour l'historique
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData._id;
      
      await axios.put(
        `/api/report/${reportId}/rubrics`,
        { 
          rubrics: updatedRubrics,
          modifiedRubricId: rubricToDelete,
          changeType: 'delete',
          deletedRubric: deletedRubricData // Envoyer les données de la rubrique supprimée
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          rubrics: updatedRubrics
        });
        
        socket.emit('broadcastReportUpdate', {
          number: report.number,
          year: report.year,
          rubrics: updatedRubrics
        });
      }

      // Log de la suppression de rubrique avec le contenu
      logAction(LOG_ACTIONS.RUBRIC_DELETE, formatReportNumber(report.number, report.year), {
        rubricId: rubricToDelete,
        rubricTitle: deletedRubricData?.title || null,
        rubricContent: deletedRubricData?.content?.substring(0, 2000) || null
      });
    } catch (error) {
      console.error('Erreur lors de la suppression de la rubrique:', error);
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // Fonction pour mettre à jour l'état d'édition (sauvegarde en BDD + WebSocket)
  // Important : on sauvegarde aussi en BDD pour que les nouveaux utilisateurs qui se connectent
  // voient les rubriques déjà verrouillées par d'autres utilisateurs
  const updateEditingState = async (updatedRubrics) => {
    try {
      console.log('Mise à jour de l\'état d\'édition...', updatedRubrics);
      
      // Mise à jour locale
      setReportData(prevData => {
        if (!prevData) return null;
        return {
          ...prevData,
          rubrics: updatedRubrics
        };
      });

      // Sauvegarde en base de données pour que les nouveaux utilisateurs
      // qui se connectent voient les rubriques verrouillées
      const token = sessionStorage.getItem('authToken');
      if (token && reportData?._id) {
        try {
          await axios.put(
            `/api/report/${reportData._id}/rubrics`,
            { rubrics: updatedRubrics },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('État d\'édition sauvegardé en BDD');
        } catch (apiError) {
          console.error('Erreur lors de la sauvegarde de l\'état d\'édition en BDD:', apiError);
          // Continue quand même avec l'émission WebSocket
        }
      }
      
      // Émission WebSocket pour informer les autres utilisateurs déjà connectés
      if (socket) {
        console.log('Émission WebSocket updateReport pour état d\'édition');
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          rubrics: updatedRubrics
        });

        socket.emit('broadcastReportUpdate', {
          number: report.number,
          year: report.year,
          rubrics: updatedRubrics
        });
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'état d\'édition:', error);
    }
  };

  // Fonction pour commencer l'édition d'une rubrique
  const startEditing = (rubric) => {
    // Modifier la vérification pour gérer le cas où user est directement le matricule
    const userMatricule = typeof user === 'object' ? user?.matricule : user;
    
    if (!userMatricule) {
      console.error('Utilisateur non connecté ou matricule manquant');
      return;
    }
    
    console.log('Début édition de la rubrique:', rubric.id, 'par utilisateur:', userMatricule);
    
    const updatedRubrics = [...reportData.rubrics];
    const index = updatedRubrics.findIndex(r => r.id === rubric.id);
    
    if (index !== -1) {
      // S'assurer que les champs requis sont présents
      if (updatedRubrics[index].generated === undefined) {
        console.log('Initialisation du champ generated qui était manquant');
        updatedRubrics[index].generated = true; // valeur par défaut
      }
      
      if (updatedRubrics[index].version === undefined) {
        console.log('Initialisation du champ version qui était manquant');
        updatedRubrics[index].version = '';
      }
      
      // Mettre à jour l'état d'édition
      updatedRubrics[index] = {
        ...updatedRubrics[index],
        isEditing: true,
        editedBy: userMatricule
      };

      // Normaliser le HTML du contenu pour préserver les espaces visibles dans l'éditeur
      setEditContents(prev => ({
        ...prev,
        [rubric.id]: normalizeEditorHtmlSpaces(prev[rubric.id] ?? updatedRubrics[index].content ?? '')
      }));
      
      // Créer une copie stable pour les rubriques en génération (generated = false)
      if (!updatedRubrics[index].generated) {
        setStableRubrics(prev => ({
          ...prev,
          [rubric.id]: { ...updatedRubrics[index] }
        }));
      }
      
      // Notifier le contexte qu'une rubrique est en cours d'édition
      startEditingRubric(rubric.id);
      
      console.log('Rubrique mise à jour:', updatedRubrics[index]);
      updateEditingState(updatedRubrics); // Utiliser la nouvelle fonction
    } else {
      console.error('Rubrique non trouvée:', rubric.id);
    }
  };

  // Fonction pour annuler l'édition d'une rubrique
  const cancelEditing = (rubric) => {
    console.log('Annulation édition de la rubrique:', rubric.id);
    
    const updatedRubrics = [...reportData.rubrics];
    const index = updatedRubrics.findIndex(r => r.id === rubric.id);
    
    if (index !== -1) {
      updatedRubrics[index] = {
        ...updatedRubrics[index],
        isEditing: false,
        editedBy: null
      };
      
      // Restaurer les valeurs originales dans les états d'édition
      setEditTitles(prev => ({
        ...prev,
        [rubric.id]: updatedRubrics[index].title
      }));
      
      setEditContents(prev => ({
        ...prev,
        [rubric.id]: updatedRubrics[index].content
      }));
      
      // Nettoyer la copie stable
      setStableRubrics(prev => {
        const newStableRubrics = { ...prev };
        delete newStableRubrics[rubric.id];
        return newStableRubrics;
      });
      
      // Notifier le contexte que cette rubrique n'est plus en cours d'édition
      stopEditingRubric(rubric.id);
      
      // Log de l'annulation de l'édition
      logAction(LOG_ACTIONS.RUBRIC_EDIT_CANCEL, formatReportNumber(report.number, report.year), {
        rubricId: rubric.id,
        rubricTitle: updatedRubrics[index].title
      });
      
      console.log('Rubrique mise à jour après annulation:', updatedRubrics[index]);
      updateEditingState(updatedRubrics); // Utiliser la nouvelle fonction
    } else {
      console.error('Rubrique non trouvée:', rubric.id);
    }
  };

  // Fonction pour sauvegarder l'édition d'une rubrique
  const saveEditing = (rubric) => {
    console.log('Sauvegarde édition de la rubrique:', rubric.id, 'avec titre:', editTitles[rubric.id]);
    
    const updatedRubrics = [...reportData.rubrics];
    const index = updatedRubrics.findIndex(r => r.id === rubric.id);
    
    if (index !== -1) {
      updatedRubrics[index] = {
        ...updatedRubrics[index],
        title: editTitles[rubric.id],
        content: editContents[rubric.id],
        isEditing: false,
        editedBy: null
      };
      
      // Notifier le contexte que cette rubrique n'est plus en cours d'édition
      stopEditingRubric(rubric.id);
      
      console.log('Rubrique mise à jour après sauvegarde:', updatedRubrics[index]);

      // Log de la modification de rubrique (titre et/ou contenu)
      const originalRubric = reportData.rubrics.find(r => r.id === rubric.id);
      const titleChanged = originalRubric?.title !== editTitles[rubric.id];
      const contentChanged = originalRubric?.content !== editContents[rubric.id];
      
      if (titleChanged) {
        logAction(LOG_ACTIONS.RUBRIC_EDIT_TITLE, formatReportNumber(report.number, report.year), {
          rubricId: rubric.id,
          oldTitle: originalRubric?.title || null,
          newTitle: editTitles[rubric.id]
        });
      }
      
      if (contentChanged) {
        // Extraire le texte brut du contenu HTML pour le log
        const extractText = (html) => {
          if (!html) return '';
          const div = document.createElement('div');
          div.innerHTML = html;
          return div.textContent || div.innerText || '';
        };
        
        // Extraire les références d'images du contenu HTML
        const extractImageRefs = (html) => {
          if (!html) return [];
          const div = document.createElement('div');
          div.innerHTML = html;
          return Array.from(div.querySelectorAll('img')).map(img => img.src);
        };
        
        const oldContentText = extractText(originalRubric?.content || '');
        const newContentText = extractText(editContents[rubric.id] || '');
        const oldImages = extractImageRefs(originalRubric?.content || '');
        const newImages = extractImageRefs(editContents[rubric.id] || '');
        
        // Calculer les images ajoutées et supprimées
        const imagesAjoutees = newImages.filter(src => !oldImages.includes(src));
        const imagesSupprimees = oldImages.filter(src => !newImages.includes(src));
        
        logAction(LOG_ACTIONS.RUBRIC_EDIT_CONTENT, formatReportNumber(report.number, report.year), {
          rubricId: rubric.id,
          rubricTitle: editTitles[rubric.id],
          ancienContenuTexte: oldContentText.substring(0, 500),
          nouveauContenuTexte: newContentText.substring(0, 500),
          ancienContenuHtml: (originalRubric?.content || '').substring(0, 2000),
          nouveauContenuHtml: (editContents[rubric.id] || '').substring(0, 2000),
          imagesAjoutees: imagesAjoutees,
          imagesSupprimees: imagesSupprimees
        });
      }
      
      // Log si aucune modification n'a été faite
      if (!titleChanged && !contentChanged) {
        logAction(LOG_ACTIONS.RUBRIC_EDIT_NO_CHANGE, formatReportNumber(report.number, report.year), {
          rubricId: rubric.id,
          rubricTitle: editTitles[rubric.id]
        });
      }

      // Passer l'ID de la rubrique modifiée pour l'historique
      updateRubrics(updatedRubrics, rubric.id, 'update');
    } else {
      console.error('Rubrique non trouvée:', rubric.id);
    }
  };

  // Fonction pour mettre à jour les rubriques via API et WebSocket
  // modifiedRubricId et changeType sont optionnels - utilisés pour l'historique
  const updateRubrics = async (updatedRubrics, modifiedRubricId = null, changeType = null) => {
    try {
      console.log('Mise à jour des rubriques...', updatedRubrics);
      
      // Vérifions d'abord si les données sont correctement structurées
      if (!Array.isArray(updatedRubrics)) {
        console.error('Format incorrect des rubriques: ce n\'est pas un tableau', updatedRubrics);
        return;
      }
      
      // Vérifier que chaque rubrique contient les champs obligatoires
      const validRubrics = updatedRubrics.map(rubric => {
        // S'assurer que tous les champs requis sont présents
        const validRubric = {
          ...rubric,
          id: rubric.id || new Date().getTime().toString(),
          position: rubric.position || 0,
          title: rubric.title || 'Sans titre',
          content: rubric.content || '',
          version: rubric.version || '',
          generated: rubric.generated !== undefined ? rubric.generated : true,
          isEditing: rubric.isEditing || false,
          editedBy: rubric.editedBy || null
        };
        return validRubric;
      });
      
      console.log('Rubriques validées:', validRubrics);
      
      // Mise à jour locale
      setReportData(prevData => {
        console.log('Mise à jour locale des données du rapport');
        return {
          ...prevData,
          rubrics: validRubrics
        };
      });
      
      // Vérifions que nous avons bien toutes les informations nécessaires
      const token = sessionStorage.getItem('authToken');
      if (!token) {
        console.error('Token d\'authentification non trouvé');
        return;
      }
      
      if (!reportData) {
        console.error('Données du rapport non disponibles');
        return;
      }
      
      // Si l'ID du rapport n'est pas disponible, essayons de le récupérer par number/year
      if (!reportData._id) {
        console.warn('ID du rapport non disponible dans reportData, tentative de recherche par number/year');
        if (!report || !report.number || !report.year) {
          console.error('Impossible de récupérer number/year du rapport');
          return;
        }
        
        try {
          console.log('Récupération du rapport par number/year:', report.number, report.year);
          const response = await axios.get(`/api/report/search`, {
            params: { number: report.number, year: report.year },
            headers: { Authorization: `Bearer ${token}` },
          });
          
          if (!response.data || !response.data._id) {
            console.error('Impossible de récupérer l\'ID du rapport');
            return;
          }
          
          // Utilisation de l'ID récupéré
          const reportId = response.data._id;
          console.log('ID du rapport récupéré:', reportId);
          
          // Continuer avec la mise à jour
          await updateReportRubrics(reportId, validRubrics, token, modifiedRubricId, changeType);
        } catch (error) {
          console.error('Erreur lors de la récupération du rapport:', error);
        }
      } else {
        // Utilisation de l'ID déjà disponible
        const reportId = reportData._id;
        console.log('Utilisation de l\'ID du rapport déjà disponible:', reportId);
        
        // Continuer avec la mise à jour
        await updateReportRubrics(reportId, validRubrics, token, modifiedRubricId, changeType);
      }
    } catch (error) {
      console.error('Erreur générale lors de la mise à jour des rubriques:', error);
    }
  };
  
  // Fonction pour envoyer la mise à jour des rubriques au serveur
  // modifiedRubricId et changeType sont optionnels - utilisés pour l'historique
  const updateReportRubrics = async (reportId, rubrics, token, modifiedRubricId = null, changeType = null) => {
    try {
      console.log('Envoi de la requête de mise à jour au serveur, ID du rapport:', reportId);
      console.log('URL complète:', `/api/report/${reportId}/rubrics`);
      console.log('Données envoyées:', { rubrics, modifiedRubricId, changeType });
      
      const response = await axios.put(
        `/api/report/${reportId}/rubrics`,
        { rubrics, modifiedRubricId, changeType },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      console.log('Réponse du serveur:', response.data);
      
      // Enregistrer la statistique de document modifié (avec déduplication par jour)
      try {
        await axios.post(
          '/api/stats',
          { 
            type: 'document_modified', 
            value: 1,
            reportKey: `${report.number}-${report.year}`
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } catch (statsError) {
        // Ne pas bloquer si l'enregistrement des stats échoue
        console.warn('Échec enregistrement statistique:', statsError);
      }
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        console.log('Émission WebSocket updateReport');
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          rubrics
        });
        
        console.log('Émission WebSocket broadcastReportUpdate');
        socket.emit('broadcastReportUpdate', {
          number: report.number,
          year: report.year,
          rubrics
        });
      } else {
        console.warn('Socket non disponible');
      }
    } catch (requestError) {
      console.error('Erreur lors de la requête API:', requestError);
      console.error('Statut de l\'erreur:', requestError.response?.status);
      console.error('Détails:', requestError.response?.data);
    }
  };

  // Fonction pour gérer les changements de titre
  const handleTitleChange = (rubricId, value) => {
    setEditTitles(prev => ({
      ...prev,
      [rubricId]: value
    }));
  };

  // Fonction pour gérer les changements de contenu
  const handleContentChange = (rubricId, value) => {
    console.log('Mise à jour contenu pour rubrique:', rubricId);
    console.log('Nouvelle valeur HTML:', value);

    setEditContents(prev => ({
      ...prev,
      [rubricId]: value
    }));
  };

  // Fonction pour supprimer les balises HTML et convertir en texte brut
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';

  // Créer un élément DOM temporaire pour parser le HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Récupérer le texte brut
  const textContent = tempDiv.textContent || tempDiv.innerText || '';

  // Nettoyer les espaces multiples et les retours à la ligne
  return textContent
    .replace(/\s+/g, ' ')  // Remplacer les espaces multiples par un seul espace
    .replace(/\n\s*\n/g, '\n')  // Nettoyer les lignes vides multiples
    .trim();
};

// Fonction pour gérer le clic sur le bouton "Citer"
const handleCiteRubric = (rubric) => {
  if (!reportData || !reportData.rubrics) return;

  // Trouver la position de la rubrique dans la liste triée
  const sortedRubrics = [...reportData.rubrics].sort((a, b) => a.position - b.position);
  const position = sortedRubrics.findIndex(r => r.id === rubric.id) + 1;

  // Convertir le contenu HTML en texte brut
  const cleanContent = stripHtmlTags(rubric.content);

  // Créer le texte complet pour l'IA (invisible pour l'utilisateur)
  const fullText = `Concerne la rubrique en position ${position}, titre : "${rubric.title}" et contenu : "${cleanContent}" ;`;

  // Créer le texte visible (seulement le titre avec une puce)
  const visibleText = rubric.title;

  // Ajouter la citation avec métadonnées
  addCitation({
    title: visibleText,
    visibleText: visibleText,
    fullText: fullText
  });
};



    // Fonction pour formater la date et heure de début (StartDate)
    const formatStartDate = (dateString) => {
      if (!dateString) return 'Non défini';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        // Format de date: jour mois année
        const dateFormatee = date.toLocaleDateString('fr-FR', {
          weekday: "long",
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
        
        // Heure et minutes au format HH:MM
        const heures = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const heureFormatee = `${heures}:${minutes}`;
        
        // Ajouter un "s" à "heure" si l'heure est 2 ou plus
        const pluriel = date.getHours() >= 2 ? 's' : '';
        
        return `${dateFormatee} à ${heureFormatee} heure${pluriel}`;
      } catch (error) {
        console.error('Erreur lors du formatage de la date de début:', error);
        return dateString;
      }
    };
  
    // Fonction pour formater la date de fin (EndDate)
    const formatEndDate = (dateString) => {
      if (!dateString) return 'Non défini';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        // Format de date: jour mois année
        return date.toLocaleDateString('fr-FR', {
          weekday: "long",
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      } catch (error) {
        console.error('Erreur lors du formatage de la date de fin:', error);
        return dateString;
      }
    };

  // ========== GESTION DES COMMENTAIRES ==========
  
  // Basculer l'affichage de la zone de commentaire
  const toggleCommentBox = (rubricId) => {
    setOpenCommentBoxes(prev => ({
      ...prev,
      [rubricId]: !prev[rubricId]
    }));
  };

  // Gérer le changement de texte du commentaire
  const handleCommentTextChange = (rubricId, value) => {
    setCommentTexts(prev => ({
      ...prev,
      [rubricId]: value
    }));
  };

  // Ajouter un commentaire
  const addComment = async (rubricId) => {
    const commentText = commentTexts[rubricId]?.trim();
    if (!commentText) return;

    const userMatricule = typeof user === 'object' ? user?.matricule : user;
    if (!userMatricule) return;

    const newComment = {
      id: new Date().getTime().toString() + Math.random().toString(36).substr(2, 9),
      userId: userMatricule,
      message: commentText,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Mise à jour locale
    const updatedRubrics = reportData.rubrics.map(rubric => {
      if (rubric.id === rubricId) {
        return {
          ...rubric,
          comments: [...(rubric.comments || []), newComment]
        };
      }
      return rubric;
    });

    setReportData(prevData => ({
      ...prevData,
      rubrics: updatedRubrics
    }));

    // Réinitialiser le champ de texte
    setCommentTexts(prev => ({
      ...prev,
      [rubricId]: ''
    }));

    // Sauvegarder et diffuser
    await saveCommentsToServer(updatedRubrics, rubricId);

    // Log de l'ajout de commentaire
    const rubric = reportData.rubrics.find(r => r.id === rubricId);
    logAction(LOG_ACTIONS.COMMENT_ADD, formatReportNumber(report.number, report.year), {
      rubricId: rubricId,
      rubricTitle: rubric?.title || null,
      commentaire: commentText.substring(0, 500)
    });
  };

  // Modifier un commentaire
  const updateComment = async (rubricId, commentId, newMessage) => {
    if (!newMessage?.trim()) return;

    // Récupérer le contenu avant modification pour le log
    const rubric = reportData.rubrics.find(r => r.id === rubricId);
    const originalComment = rubric?.comments?.find(c => c.id === commentId);
    const oldMessage = originalComment?.message || '';

    const updatedRubrics = reportData.rubrics.map(rubric => {
      if (rubric.id === rubricId) {
        return {
          ...rubric,
          comments: (rubric.comments || []).map(comment => {
            if (comment.id === commentId) {
              return {
                ...comment,
                message: newMessage.trim(),
                updatedAt: new Date().toISOString()
              };
            }
            return comment;
          })
        };
      }
      return rubric;
    });

    setReportData(prevData => ({
      ...prevData,
      rubrics: updatedRubrics
    }));

    // Fermer l'édition
    setEditingComments(prev => {
      const newState = { ...prev };
      delete newState[commentId];
      return newState;
    });

    // Sauvegarder et diffuser
    await saveCommentsToServer(updatedRubrics, rubricId);

    // Log de la modification de commentaire avec contenu avant/après
    logAction(LOG_ACTIONS.COMMENT_EDIT, formatReportNumber(report.number, report.year), {
      rubricId: rubricId,
      rubricTitle: rubric?.title || null,
      commentId: commentId,
      ancienContenu: oldMessage.substring(0, 500),
      nouveauContenu: newMessage.trim().substring(0, 500)
    });
  };

  // Supprimer un commentaire
  const deleteComment = async (rubricId, commentId) => {
    // Récupérer le contenu du commentaire avant suppression pour le log
    const rubric = reportData.rubrics.find(r => r.id === rubricId);
    const commentToDelete = rubric?.comments?.find(c => c.id === commentId);
    const deletedContent = commentToDelete?.message || '';

    const updatedRubrics = reportData.rubrics.map(rubric => {
      if (rubric.id === rubricId) {
        return {
          ...rubric,
          comments: (rubric.comments || []).filter(comment => comment.id !== commentId)
        };
      }
      return rubric;
    });

    setReportData(prevData => ({
      ...prevData,
      rubrics: updatedRubrics
    }));

    // Sauvegarder et diffuser
    await saveCommentsToServer(updatedRubrics, rubricId);

    // Log de la suppression de commentaire avec le contenu supprimé
    logAction(LOG_ACTIONS.COMMENT_DELETE, formatReportNumber(report.number, report.year), {
      rubricId: rubricId,
      rubricTitle: rubric?.title || null,
      commentId: commentId,
      contenuSupprime: deletedContent.substring(0, 500)
    });
  };

  // Sauvegarder les commentaires sur le serveur et diffuser via WebSocket
  const saveCommentsToServer = async (updatedRubrics, rubricId) => {
    try {
      const token = sessionStorage.getItem('authToken');
      if (!token || !reportData?._id) return;

      await axios.put(
        `/api/report/${reportData._id}/rubrics`,
        { rubrics: updatedRubrics },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Émission WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          rubrics: updatedRubrics
        });

        socket.emit('broadcastReportUpdate', {
          number: report.number,
          year: report.year,
          rubrics: updatedRubrics
        });
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des commentaires:', error);
    }
  };

  // Commencer l'édition d'un commentaire
  const startEditingComment = (commentId, currentMessage) => {
    setEditingComments(prev => ({
      ...prev,
      [commentId]: currentMessage
    }));
  };

  // Annuler l'édition d'un commentaire
  const cancelEditingComment = (commentId) => {
    setEditingComments(prev => {
      const newState = { ...prev };
      delete newState[commentId];
      return newState;
    });
  };

  // Formater la date relative pour les commentaires
  const formatCommentDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // ========== FIN GESTION DES COMMENTAIRES ==========

  // Afficher un indicateur de chargement
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        p: 3,
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : 'inherit'
      }}>
        <CircularProgress sx={{
          color: isDark ? '#4fc3f7' : undefined
        }} />
      </Box>
    );
  }

  // Afficher un message d'erreur
  if (loadError && !reportData) {
    return (
      <Box sx={{ 
        p: 3,
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : 'inherit'
      }}>
        <Alert 
          severity="error"
          sx={{
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : undefined,
            color: isDark ? '#ff6b6b' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#ff6b6b' : undefined
            }
          }}
        >
          {loadError}
        </Alert>
      </Box>
    );
  }

  // Afficher un message si aucun rapport n'est sélectionné
  if (!report) {
    return (
      <Box sx={{ 
        p: 3,
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : 'inherit'
      }}>
        <Alert 
          severity="info"
          sx={{
            backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : undefined,
            color: isDark ? '#4fc3f7' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#4fc3f7' : undefined
            }
          }}
        >
          Veuillez sélectionner un rapport pour afficher son contenu.
        </Alert>
      </Box>
    );
  }

  // Afficher un message si aucune rubrique n'est disponible
  if (!reportData || !reportData.rubrics || reportData.rubrics.length === 0) {
    return (
      <EmptyReportGuide
        title="Commencez à rédiger"
        icon={<EditIcon sx={{ fontSize: 40, color: '#fff' }} />}
        step3Text="Modifiez-les ici"
      />
    );
  }

  // Tri des rubriques par position
  const sortedRubrics = [...reportData.rubrics].sort((a, b) => a.position - b.position);

  // Fonction pour déterminer si une rubrique est de type GuardIA
  const isGuardiaRubric = (rubric) => {
    return rubric.title === 'Rubrique GuardIA' || rubric.isGuardia;
  };

  // Rendu du composant avec les rubriques
  return (
    <Box 
      ref={containerRef}
      sx={{ 
        p: isEditorFullscreen ? 0 : 3, 
        height: '100%', 
        overflow: isEditorFullscreen ? 'hidden' : 'auto',
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : '#f0f2f5'
      }}>
        {/* Masquer l'erreur en mode fullscreen */}
        {reportError && !isEditorFullscreen && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              backgroundColor: isDark ? 'rgba(255, 193, 7, 0.15)' : undefined,
              color: isDark ? '#ffb74d' : undefined,
              '& .MuiAlert-icon': {
                color: isDark ? '#ffb74d' : undefined
              }
            }}
          >
            {reportError}
          </Alert>
        )}
        {/* Masquer l'introduction en mode fullscreen */}
        <Paper 
          elevation={1} 
          sx={{ 
            mb: 2, 
            p: 2,
            display: isEditorFullscreen ? 'none' : 'block',
            backgroundColor: isDark ? '#22303e' : '#ffffff',
            color: isDark ? '#ecf0f1' : 'inherit',
            borderColor: isDark ? '#2d3f4c' : 'transparent'
          }}
        >
          <Typography variant="caption" sx={{ 
            fontWeight: 'bold', 
            mb: 1,
            color: isDark ? '#4fc3f7' : 'inherit'
          }}>
            Introduction
          </Typography>

          <Typography variant="body1" sx={{
            whiteSpace: 'pre-wrap',
            '& p': { marginTop: 0 },
            fontSize: '15px',
            color: isDark ? '#ecf0f1' : 'inherit'
          }}>
            Ce jourd'hui, le {formatStartDate(reportData.StartDate)},<br/>
            Nous soussigné{reportData.intervenants && 
              reportData.intervenants.filter(intervenant => intervenant.sign === true).length > 1 ? 's' : ''}, {reportData.intervenants && reportData.intervenants
              .filter(intervenant => intervenant.sign === true)
              .map((intervenant, index, array) => (
                <Typography component="span" key={index}>
                  <Typography component="span" sx={{
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>
                    {intervenant.lastname}
                  </Typography>
                  {' '}
                  <Typography component="span" sx={{
                    fontWeight: 'bold',
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>
                    {intervenant.firstname}
                  </Typography>
                  <Typography component="span" sx={{
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>, </Typography>
                  <Typography component="span" sx={{
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>{intervenant.rank}</Typography>
                  <Typography component="span" sx={{
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>{index < array.length - 1 ? ', ' : ''}</Typography>
                </Typography>
              ))}
            <br/>
            attaché{reportData.intervenants && 
              reportData.intervenants.filter(intervenant => intervenant.sign === true).length > 1 ? 's' : ''} au service {reportData.unit} de la <SettingsFieldDisplay fieldName="zoneNamePV" />,<br/>
            portons à la connaissance de votre office :
          </Typography>

        </Paper>
      
      {sortedRubrics.map((rubric, index) => {
        // Déterminer le style de bordure et de fond selon l'état
        let borderColor = 'transparent';
        if (rubric.isEditing) {
          const userMatricule = typeof user === 'object' ? user?.matricule : user;
          if (rubric.editedBy === userMatricule) {
            borderColor = '#e67e22';
          } else {
            borderColor = '#3b82f6'; // bleu
          }
        } else if (!rubric.generated) {
          // Pour les rubriques GuardIA non générées, utiliser la couleur mauve
          if (isGuardiaRubric(rubric)) {
            borderColor = isDark ? '#c084fc' : '#800080';
          } else {
            borderColor = '#ef4444'; // rouge
          }
        }
        
        // Déterminer si l'utilisateur actuel est l'éditeur
        const userMatricule = typeof user === 'object' ? user?.matricule : user;
        const isCurrentUserEditor = rubric.editedBy === userMatricule;
        
        // Masquer les rubriques qui ne sont pas en cours d'édition par l'utilisateur actuel
        // On garde visible la rubrique avec l'éditeur TinyMCE pour que le fullscreen fonctionne
        const isBeingEditedByMe = rubric.isEditing && isCurrentUserEditor;
        const shouldHideForFullscreen = isEditorFullscreen && !isBeingEditedByMe;

        return (
          <Box 
            key={rubric.id} 
            sx={{ 
              mb: 2,
              // Utiliser visibility au lieu de display pour ne pas casser le fullscreen TinyMCE
              ...(shouldHideForFullscreen && {
                visibility: 'hidden',
                position: 'absolute',
                zIndex: -9999,
                pointerEvents: 'none'
              })
            }}
          >
          <Paper 
            elevation={1}
            data-rubric-id={rubric.id}
            ref={el => rubricRefs.current[rubric.id] = el}
            sx={{ 
              position: 'relative',
              zIndex: 1,
              p: 2,
              borderLeft: borderColor !== 'transparent' ? `4px solid ${borderColor}` : 'none',
              backgroundColor: isDark ? '#22303e' : '#ffffff',
              color: isDark ? '#ecf0f1' : 'inherit',
              transition: 'all 0.3s ease',
              boxShadow: 'none',
              '&:hover': borderColor !== 'transparent' ? {
                boxShadow: `0 0 12px 2px ${borderColor}50`
              } : {},
              '& .MuiDivider-root': {
                borderColor: isDark ? '#2d3f4c' : 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ display: 'flex', mr: 2 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => moveRubric(rubric.id, 'up')}
                    disabled={index === 0 || rubric.isEditing}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => moveRubric(rubric.id, 'down')}
                    disabled={index === sortedRubrics.length - 1 || rubric.isEditing}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </Box>
                
                {/* Affichage du titre (normal ou champ d'édition) */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <Badge
                    badgeContent={`#${index + 1}`}
                    color="primary"
                    sx={{
                      mr: 2.5,
                      '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        minWidth: '22px',
                        height: '18px',
                        backgroundColor: isDark ? '#555555' : '#cccccc',
                        color: isDark ? '#ecf0f1' : '#666666'
                      }
                    }}
                  />
                  {rubric.isEditing && isCurrentUserEditor && rubric.generated ? (
                    <TextField
                      fullWidth
                      variant="standard"
                      value={editTitles[rubric.id] || ''}
                      onChange={(e) => handleTitleChange(rubric.id, e.target.value)}
                      sx={{
                        fontWeight: 'bold',
                        '& .MuiInput-root': {
                          color: isDark ? '#ecf0f1' : 'inherit',
                          '&:before': {
                            borderBottomColor: isDark ? '#2d3f4c' : 'rgba(0, 0, 0, 0.42)'
                          },
                          '&:hover:not(.Mui-disabled):before': {
                            borderBottomColor: isDark ? '#4fc3f7' : 'rgba(0, 0, 0, 0.87)'
                          },
                          '&.Mui-focused:after': {
                            borderBottomColor: isDark ? '#4fc3f7' : '#1976d2'
                          }
                        },
                        '& .MuiInput-input': {
                          color: isDark ? '#ecf0f1' : 'inherit',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  ) : (
                    <Typography variant="subtitle1" sx={{
                      fontWeight: 'bold',
                      color: isDark ? '#ecf0f1' : 'inherit',
                      flexGrow: 1
                    }}>
                      {rubric.title}
                    </Typography>
                  )}
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {/* Bouton Citer - uniquement pour les rubriques générées */}
                {false && rubric.generated && (
                  <Button
                    variant="contained"
                    style={{
                      fontSize: '15px'
                    }}
                    startIcon={
                      <GuardiaLogo
                        style={{
                          width: '16px',
                          height: '16px',
                          color: 'white'
                        }}
                      />
                    }
                    onClick={() => {
                      if (!guardiaChatAccess.hasAccess) {
                        alert(guardiaChatAccess.reason);
                      } else {
                        handleCiteRubric(rubric);
                      }
                    }}
                    disabled={!guardiaChatAccess.hasAccess}
                    size="small"
                    sx={{
                      mr: 2,
                      backgroundColor: guardiaChatAccess.hasAccess ? (isDark ? '#c084fc' : '#800080') : (isDark ? '#555' : '#ccc'),
                      color: 'white',
                      borderRadius: '8px',
                      '&:hover': guardiaChatAccess.hasAccess ? {
                        backgroundColor: '#9932cc',
                      } : {},
                      '& .MuiButton-startIcon': {
                        color: 'white'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: isDark ? '#555' : '#ccc',
                        color: 'white'
                      }
                    }}
                  >
                    Citer
                  </Button>
                )}
                
                {/* Bouton Annuler (visible uniquement si l'utilisateur est l'éditeur) */}
                {rubric.isEditing && isCurrentUserEditor && (
                  <Button
                    variant="contained"
                    style={{
                      fontSize: '15px'
                    }}
                    startIcon={<CloseIcon />}
                    onClick={() => handleOpenCancelDialog(rubric.id)}
                    size="small"
                    sx={{ 
                      mr: 2,
                      backgroundColor: isDark ? '#7f8c8d' : 'grey.500',
                      color: 'white',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: isDark ? '#95a5a6' : 'grey.700',
                      }
                    }}
                  >
                    Annuler
                  </Button>
                )}
                
                {/* Bouton principal qui change selon le contexte */}
                {rubric.generated ? (
                  // Cas pour rubric.generated = true
                  rubric.isEditing ? (
                    isCurrentUserEditor ? (
                      // Bouton Enregistrer
                      <Button
                        variant="contained"
                        style={{
                          fontSize: '15px'
                        }}
                        startIcon={<SaveIcon />}
                        onClick={() => saveEditing(rubric)}
                        size="small"
                        sx={{ 
                          mr: 2,
                          backgroundColor: '#e67e22',
                          color: 'white',
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: '#d35400',
                          }
                        }}
                      >
                        Enregistrer
                      </Button>
                    ) : (
                      // Bouton Modifier désactivé
                      <Button
                        variant="contained"
                        style={{
                          fontSize: '15px'
                        }}
                        startIcon={<EditIcon />}
                        disabled
                        size="small"
                        sx={{ 
                          mr: 2,
                          borderRadius: '8px',
                          '&.Mui-disabled': {
                            backgroundColor: isDark ? '#22303e' : undefined,
                            color: isDark ? '#7f8c8d' : undefined
                          }
                        }}
                      >
                        Modifier
                      </Button>
                    )
                  ) : (
                    // Bouton Modifier normal
                    <Button
                      variant="contained"
                      style={{
                        fontSize: '15px'
                      }}
                      startIcon={<EditIcon />}
                      onClick={() => startEditing(rubric)}
                      size="small"
                      sx={{ 
                        mr: 2,
                        backgroundColor: isDark ? '#27ae60' : 'green',
                        color: 'white',
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: isDark ? '#2ecc71' : 'darkgreen',
                        }
                      }}
                    >
                      Modifier
                    </Button>
                  )
                ) : (
                  // Cas pour rubric.generated = false
                  (!rubric.isEditing || !isCurrentUserEditor) && (
                    // Bouton Générer - couleur différente pour les rubriques GuardIA
                    <Button
                      variant="contained"
                      style={{
                        fontSize: '15px'
                      }}
                      startIcon={<AutorenewIcon />}
                      onClick={() => startEditing(rubric)}
                      disabled={rubric.isEditing && !isCurrentUserEditor}
                      size="small"
                      sx={{
                        mr: 2,
                        backgroundColor: isGuardiaRubric(rubric)
                          ? (isDark ? '#c084fc' : '#800080')
                          : (isDark ? '#e74c3c' : 'red'),
                        color: 'white',
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: isGuardiaRubric(rubric)
                            ? '#9932cc'
                            : (isDark ? '#c0392b' : 'darkred'),
                        },
                        '&.Mui-disabled': {
                          backgroundColor: isDark ? '#22303e' : undefined,
                          color: isDark ? '#7f8c8d' : undefined
                        }
                      }}
                    >
                      Générer
                    </Button>
                  )
                )}
                
                {/* Bouton Commentaire (mégaphone) */}
                <Tooltip title="Ajouter un commentaire" arrow>
                  <IconButton
                    size="small"
                    onClick={() => toggleCommentBox(rubric.id)}
                    sx={{ 
                      mr: 1,
                      color: 'white',
                      backgroundColor: openCommentBoxes[rubric.id] 
                        ? (isDark ? '#3498db' : '#2980b9')
                        : (isDark ? '#5dade2' : '#3498db'),
                      '&:hover': {
                        backgroundColor: isDark ? '#2980b9' : '#2471a3',
                      },
                      width: '32px',
                      height: '32px',
                      position: 'relative'
                    }}
                  >
                    <Badge 
                      badgeContent={(rubric.comments || []).length} 
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          minWidth: '16px',
                          height: '16px',
                          top: -4,
                          right: -4
                        }
                      }}
                    >
                      <CampaignIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>

                <IconButton
                  size="small"
                  onClick={() => handleOpenDeleteDialog(rubric.id)}
                  disabled={rubric.isEditing}
                  sx={{ 
                    color: 'white',
                    backgroundColor: rubric.isEditing 
                      ? (isDark ? '#22303e' : 'grey.400')
                      : (isDark ? '#e74c3c' : 'error.main'),
                    '&:hover': {
                      backgroundColor: rubric.isEditing 
                        ? (isDark ? '#22303e' : 'grey.400')
                        : (isDark ? '#c0392b' : 'error.dark'),
                    },
                    width: '32px',
                    height: '32px'
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            
            <Divider sx={{ my: 1 }} />
            
            {/* Message de notification si quelqu'un d'autre édite */}
            {rubric.isEditing && !isCurrentUserEditor && (
              <Alert 
                severity="info" 
                                  sx={{ 
                    mb: 2,
                    backgroundColor: isDark ? '#614051' : undefined,
                    color: isDark ? '#614051' : undefined,
                    '& .MuiAlert-icon': {
                      color: isDark ? '#614051' : undefined
                    },
                    '& .MuiAlertTitle-root': {
                      color: isDark ? '#ecf0f1' : undefined
                    }
                  }}
              >
                <AlertTitle sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  color: isDark ? '#ecf0f1' : undefined
                }}>
                  <span>{rubric.generated ? 'En cours de modification' : 'En cours de génération'} par</span>
                  <Box component="span" sx={{ display: 'inline-flex', ml: 0.5 }}>
                    <GetUserAvatar matricule={rubric.editedBy} defaultValue="..." />
                  </Box>
                </AlertTitle>
              </Alert>
            )}
            
            {/* Contenu de la rubrique */}
            {rubric.generated ? (
              rubric.isEditing && isCurrentUserEditor ? (
                // Champ d'édition pour le contenu
                <Box>
                  <RichTextEditor
                    value={editContents[rubric.id] || ''}
                    onChange={(value) => handleContentChange(rubric.id, value)}
                    placeholder="Écrivez ici..."
                    isDark={isDark}
                    userInsertions={userInsertions}
                  />
                  {/* Composant GuardiaCheck pour la correction */}
                  <GuardiaCheck
                    rubric={rubric}
                    editContents={editContents}
                    guardiaAccess={guardiaAccess}
                    guardiaListenAccess={guardiaListenAccess}
                    isDark={isDark}
                    onCorrectionComplete={(rubricId, correctedText) => {
                      // Optionnel : callback si vous voulez traiter la correction terminée
                      console.log('Correction terminée pour la rubrique:', rubricId);
                    }}
                  />
                </Box>
              ) : (
                // Affichage normal du contenu généré
                <RichTextDisplay 
                  content={rubric.content} 
                  isDark={isDark}
                />
              )
            ) : (
              // Contenu pour les rubriques non générées
              rubric.isEditing && isCurrentUserEditor ? (
                <div>
                  {isGuardiaRubric(rubric) ? (
                    <GuardiaWrite
                      rubric={stableRubrics[rubric.id] || rubric}
                      reportNumber={reportData.number}
                      reportYear={reportData.year}
                      onSave={(generatedData) => {
                        // Mettre à jour le contenu et le titre de la rubrique
                        const updatedRubrics = [...reportData.rubrics];
                        const index = updatedRubrics.findIndex(r => r.id === rubric.id);

                        if (index !== -1) {
                          updatedRubrics[index] = {
                            ...updatedRubrics[index],
                            title: generatedData.title || updatedRubrics[index].title,
                            content: generatedData.content,
                            generated: true,
                            isEditing: false,
                            editedBy: null
                          };

                          // Mettre à jour les états d'édition
                          setEditTitles(prev => ({
                            ...prev,
                            [rubric.id]: generatedData.title || prev[rubric.id]
                          }));

                          setEditContents(prev => ({
                            ...prev,
                            [rubric.id]: generatedData.content
                          }));

                          // Nettoyer la copie stable
                          setStableRubrics(prev => {
                            const newStableRubrics = { ...prev };
                            delete newStableRubrics[rubric.id];
                            return newStableRubrics;
                          });

                          // Notifier le contexte que cette rubrique n'est plus en cours d'édition
                          stopEditingRubric(rubric.id);

                          // Mise à jour complète avec changeType 'generate' pour l'historique
                          updateRubrics(updatedRubrics, rubric.id, 'generate');
                        }
                      }}
                      onCancel={() => cancelEditing(rubric)}
                    />
                  ) : (
                    <RubricForm
                      rubric={stableRubrics[rubric.id] || rubric}
                      reportNumber={reportData.number}
                      reportYear={reportData.year}
                      onSave={(generatedData) => {
                        // Mettre à jour le contenu et le titre de la rubrique
                        const updatedRubrics = [...reportData.rubrics];
                        const index = updatedRubrics.findIndex(r => r.id === rubric.id);

                        if (index !== -1) {
                          updatedRubrics[index] = {
                            ...updatedRubrics[index],
                            title: generatedData.title || updatedRubrics[index].title,
                            content: generatedData.content,
                            generated: true,
                            isEditing: false,
                            editedBy: null
                          };

                          // Mettre à jour les états d'édition
                          setEditTitles(prev => ({
                            ...prev,
                            [rubric.id]: generatedData.title || prev[rubric.id]
                          }));

                          setEditContents(prev => ({
                            ...prev,
                            [rubric.id]: generatedData.content
                          }));

                          // Nettoyer la copie stable
                          setStableRubrics(prev => {
                            const newStableRubrics = { ...prev };
                            delete newStableRubrics[rubric.id];
                            return newStableRubrics;
                          });

                          // Notifier le contexte que cette rubrique n'est plus en cours d'édition
                          stopEditingRubric(rubric.id);

                          // Mise à jour complète avec changeType 'generate' pour l'historique
                          updateRubrics(updatedRubrics, rubric.id, 'generate');
                        }
                      }}
                    />
                  )}
                </div>
              ) : (
                <Typography
                  sx={{
                    color: isDark ? '#95a5a6' : 'text.disabled',
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    mt: 2
                  }}
                >
                  {isGuardiaRubric(rubric)
                    ? "Cette rubrique n'a pas encore été générée avec GuardIA."
                    : "Cette rubrique n'a pas encore été générée."
                  }
                </Typography>
              )
            )}

          </Paper>
          
          {/* ========== ZONE DE COMMENTAIRES (cadre séparé) ========== */}
          <Collapse in={openCommentBoxes[rubric.id] || false}>
            <Box
              sx={{
                position: 'relative',
                mt: -1,
                mx: 2,
                zIndex: 0,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  pt: 2,
                  backgroundColor: isDark ? '#1a2530' : '#f8f9fa',
                  borderRadius: '0 0 12px 12px',
                  borderLeft: `1px solid ${isDark ? '#3d5a73' : '#e0e0e0'}`,
                  borderRight: `1px solid ${isDark ? '#3d5a73' : '#e0e0e0'}`,
                  borderBottom: `1px solid ${isDark ? '#3d5a73' : '#e0e0e0'}`,
                  boxShadow: isDark 
                    ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
                    : '0 4px 12px rgba(0, 0, 0, 0.08)',
                }}
              >
                {/* Liste des commentaires existants */}
                {(rubric.comments || []).length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    {(rubric.comments || []).map((comment) => {
                      const userMatricule = typeof user === 'object' ? user?.matricule : user;
                      const isOwner = comment.userId === userMatricule;
                      const isEditingThisComment = editingComments[comment.id] !== undefined;

                      return (
                        <Box
                          key={comment.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 0.5,
                            py: 0.5,
                            px: 1,
                            borderRadius: '6px',
                            backgroundColor: 'transparent',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              backgroundColor: isDark ? 'rgba(79, 195, 247, 0.06)' : 'rgba(52, 152, 219, 0.04)',
                            }
                          }}
                        >
                          {/* Avatar compact */}
                          <Box sx={{ flexShrink: 0, mr: 1, transform: 'scale(0.8)', transformOrigin: 'left center' }}>
                            <GetUserAvatar matricule={comment.userId} defaultValue="?" />
                          </Box>

                          {/* Contenu du commentaire */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {isEditingThisComment ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={editingComments[comment.id] || ''}
                                  onChange={(e) => setEditingComments(prev => ({
                                    ...prev,
                                    [comment.id]: e.target.value
                                  }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      updateComment(rubric.id, comment.id, editingComments[comment.id]);
                                    }
                                    if (e.key === 'Escape') {
                                      cancelEditingComment(comment.id);
                                    }
                                  }}
                                  autoFocus
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: isDark ? '#22303e' : '#fff',
                                      '& fieldset': { borderColor: isDark ? '#3d5a73' : '#ccc' },
                                      '&.Mui-focused fieldset': { borderColor: isDark ? '#4fc3f7' : '#3498db' },
                                    },
                                    '& .MuiInputBase-input': {
                                      color: isDark ? '#ecf0f1' : 'inherit',
                                      fontSize: '0.875rem',
                                      py: 0.5,
                                    }
                                  }}
                                />
                                <IconButton size="small" onClick={() => updateComment(rubric.id, comment.id, editingComments[comment.id])} sx={{ color: isDark ? '#27ae60' : 'green', p: 0.5 }}>
                                  <SaveIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => cancelEditingComment(comment.id)} sx={{ color: isDark ? '#7f8c8d' : 'grey', p: 0.5 }}>
                                  <CloseIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: '0.875rem',
                                    color: isDark ? '#bdc3c7' : '#555',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.4
                                  }}
                                >
                                  {comment.message}
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: isDark ? '#5d6d7e' : '#aaa',
                                    ml: 0.5
                                  }}
                                >
                                  · {formatCommentDate(comment.createdAt)}
                                  {comment.updatedAt && comment.updatedAt !== comment.createdAt && ' (modifié)'}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Boutons d'actions visibles */}
                          {isOwner && !isEditingThisComment && (
                            <Box sx={{ display: 'flex', gap: 0.25, ml: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => startEditingComment(comment.id, comment.message)}
                                sx={{
                                  color: isDark ? '#5d6d7e' : '#bbb',
                                  p: 0.3,
                                  '&:hover': { color: isDark ? '#4fc3f7' : '#3498db' }
                                }}
                              >
                                <EditIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => deleteComment(rubric.id, comment.id)}
                                sx={{
                                  color: isDark ? '#5d6d7e' : '#bbb',
                                  p: 0.3,
                                  '&:hover': { color: isDark ? '#e74c3c' : '#e74c3c' }
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Champ compact pour ajouter un commentaire */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    backgroundColor: isDark ? '#22303e' : '#fff',
                    borderRadius: '4px',
                    border: `1px solid ${isDark ? '#3d5a73' : '#ddd'}`,
                    transition: 'border-color 0.2s ease',
                    '&:hover': {
                      borderColor: isDark ? '#4fc3f7' : '#3498db',
                    },
                    '&:focus-within': {
                      borderColor: isDark ? '#4fc3f7' : '#3498db',
                    }
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Ajouter un commentaire..."
                    value={commentTexts[rubric.id] || ''}
                    onChange={(e) => handleCommentTextChange(rubric.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        addComment(rubric.id);
                      }
                    }}
                    multiline
                    minRows={1}
                    maxRows={5}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        '& fieldset': { border: 'none' },
                      },
                      '& .MuiInputBase-input': {
                        color: isDark ? '#ecf0f1' : 'inherit',
                        fontSize: '0.875rem',
                        py: 0.5,
                        px: 1,
                        '&::placeholder': { color: isDark ? '#5d6d7e' : '#aaa', opacity: 1 }
                      }
                    }}
                  />
                  <IconButton
                    onClick={() => addComment(rubric.id)}
                    disabled={!commentTexts[rubric.id]?.trim()}
                    size="small"
                    sx={{
                      color: (commentTexts[rubric.id]?.trim()) 
                        ? (isDark ? '#4fc3f7' : '#3498db')
                        : (isDark ? '#3d5a73' : '#ccc'),
                      mr: 0.5,
                      p: 0.5,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: isDark ? '#5dade2' : '#2980b9',
                      },
                      '&.Mui-disabled': {
                        color: isDark ? '#3d5a73' : '#ccc',
                      }
                    }}
                  >
                    <SendIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Box>
              </Paper>
            </Box>
          </Collapse>
          {/* ========== FIN ZONE DE COMMENTAIRES ========== */}
          </Box>
        );
      })}

        {/* Masquer la conclusion en mode fullscreen */}
        <Paper 
          elevation={1} 
          sx={{ 
            mb: 2, 
            p: 2,
            display: isEditorFullscreen ? 'none' : 'block',
            backgroundColor: isDark ? '#22303e' : '#ffffff',
            color: isDark ? '#ecf0f1' : 'inherit'
          }}
        >
          <Typography variant="caption" sx={{ 
            fontWeight: 'bold', 
            mb: 1,
            color: isDark ? '#4fc3f7' : 'inherit'
          }}>
            Conclusion
          </Typography>

          <Typography variant="body1" sx={{
            whiteSpace: 'pre-wrap',
            '& p': { marginTop: 0 },
            fontSize: '15px',
            color: isDark ? '#ecf0f1' : 'inherit'
          }}>
            Dont acte, clos le {formatEndDate(reportData.EndDate)}<br/>
            
            {reportData.intervenants && reportData.intervenants
              .filter(intervenant => intervenant.sign === true)
              .map((intervenant, index) => (
                <Box key={index} sx={{ display: 'flex', mt: 1 }}>
                  <Typography component="span" sx={{
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>•{' '}</Typography>
                  <Typography component="span" sx={{
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>{intervenant.rank}{' '}</Typography>
                  <Typography component="span" sx={{
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>
                    {intervenant.lastname}{' '}
                  </Typography>
                  <Typography component="span" sx={{
                    fontWeight: 'bold',
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>
                    {intervenant.firstname}
                  </Typography>
                </Box>
              ))}
          </Typography>

        </Paper>

        {/* Boîte de dialogue de confirmation d'annulation */}
        <Dialog
          open={openCancelDialog}
          onClose={handleCloseCancelDialog}
          PaperProps={{
            sx: {
              backgroundColor: isDark ? '#22303e' : undefined,
              color: isDark ? '#ecf0f1' : undefined
            }
          }}
        >
          <DialogTitle sx={{
            color: isDark ? '#ecf0f1' : 'inherit'
          }}>
            Confirmation d'annulation
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{
              color: isDark ? '#bdc3c7' : 'inherit'
            }}>
              Êtes-vous sûr de vouloir annuler vos modifications ? Toutes les modifications non sauvegardées seront perdues.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseCancelDialog}
              color="primary"
              sx={{
                color: isDark ? '#4fc3f7' : undefined
              }}
            >
              Continuer l'édition
            </Button>
            <Button
              onClick={handleConfirmCancel}
              color="error"
              autoFocus
              sx={{
                color: isDark ? '#ff6b6b' : undefined
              }}
            >
              Annuler les modifications
            </Button>
          </DialogActions>
        </Dialog>

        {/* Boîte de dialogue de confirmation de suppression */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: {
              backgroundColor: isDark ? '#22303e' : undefined,
              color: isDark ? '#ecf0f1' : undefined
            }
          }}
        >
          <DialogTitle sx={{ 
            color: isDark ? '#ecf0f1' : 'inherit'
          }}>
            Confirmation de suppression
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ 
              color: isDark ? '#bdc3c7' : 'inherit'
            }}>
              Êtes-vous sûr de vouloir supprimer cette rubrique ? Cette action est irréversible.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDeleteDialog} 
              color="primary"
              sx={{
                color: isDark ? '#4fc3f7' : undefined
              }}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleDeleteRubric} 
              color="error" 
              autoFocus
              sx={{
                color: isDark ? '#ff6b6b' : undefined
              }}
            >
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>

    </Box>
  );
}

export default Edit;